/**
 * A product's identity mark for icon-sized surfaces (the collapsed rail,
 * sub-lg rail rows) — the Slack/Linear pattern (owner 2026-08-27: "each
 * product's icon should be its github icon. if not available, then the
 * first letter"):
 *
 *  1. A repo whose git remote lives on github.com → the OWNER's avatar
 *     (`github.com/<owner>.png` — a public, unauthenticated endpoint).
 *  2. Otherwise (local folders, zips, non-GitHub remotes, or the avatar
 *     failing to load) → a monogram: the product's first letter on the app's
 *     own accent wash. Round 9 (owner: "why is product picker green out of
 *     nowhere? Keep it consistent with app color theme") retired the
 *     name-derived hue — a random green in an all-purple app read as a bug,
 *     and the letter already does the telling-apart.
 */

import { useState } from 'react';

import type { RepoSummary } from '../../api/types';

/** github.com/<owner>/… (https or ssh) → the owner segment, else null. */
export function githubOwner(sourceRef: string | null | undefined): string | null {
  if (sourceRef === undefined || sourceRef === null) return null;
  const m = /(?:github\.com[/:])([A-Za-z0-9-]+)\//.exec(sourceRef);
  return m?.[1] ?? null;
}

/** First github.com owner across the product's repos, else null. */
export function productGithubOwner(repos: readonly RepoSummary[]): string | null {
  for (const repo of repos) {
    if (repo.source_type !== 'git') continue;
    const owner = githubOwner(repo.source_ref);
    if (owner !== null) return owner;
  }
  return null;
}

export function ProductMark({
  name,
  repos,
  size = 20,
}: {
  name: string;
  repos: readonly RepoSummary[];
  /** rendered square size in px (the switcher uses 32/24; rows keep 20) */
  size?: number;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const owner = productGithubOwner(repos);
  const dimension = { width: size, height: size };
  if (owner !== null && !imgFailed) {
    return (
      <img
        src={`https://github.com/${owner}.png?size=48`}
        alt=""
        aria-hidden
        onError={() => setImgFailed(true)}
        className="shrink-0 rounded-[5px] object-cover"
        style={dimension}
        data-testid="product-mark-avatar"
      />
    );
  }
  const letter = (name.trim()[0] ?? '?').toUpperCase();
  return (
    <span
      aria-hidden
      data-testid="product-mark-monogram"
      className="flex shrink-0 items-center justify-center rounded-[5px] font-semibold"
      style={{
        ...dimension,
        fontSize: Math.max(10, Math.round(size * 0.55)),
        background: 'var(--accent-subtle)',
        color: 'var(--accent)',
      }}
    >
      {letter}
    </span>
  );
}
